/**
 * Residual OIDC named-popup child handoff + opener resume (legacy).
 * New Google/Apple starts use same-window redirect — these hooks only help
 * leftover child windows from older builds.
 */
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  handoffOidcPopupToOpener,
  isOidcPopupChildWindow,
  isOidcPopupHandoffUrl,
  subscribeOidcPopupComplete,
} from '../shared/session/oidcPopupBridge.js';

/**
 * Popup child: when BE lands cookies + redirects into this window, hand the
 * URL to the opener and close so the main tab never full-document bounced.
 */
export function useOidcPopupChildHandoff(): void {
  useEffect(() => {
    if (typeof window === 'undefined' || !isOidcPopupChildWindow()) {
      return;
    }
    const href = window.location.href;
    if (!isOidcPopupHandoffUrl(href)) {
      return;
    }
    const params = new URL(href).searchParams;
    const ok = !(params.has('error') || params.has('oidcError'));
    handoffOidcPopupToOpener(href, ok);
  }, []);
}

/**
 * Opener: SPA-navigate to the popup's post-callback URL so cookie land / SoftRace
 * run in the original tab (no full refresh of the sign-in page).
 */
export function useOidcPopupOpenerResume(): void {
  const navigate = useNavigate();

  useEffect(() => {
    return subscribeOidcPopupComplete((message) => {
      let url: URL;
      try {
        url = new URL(message.href);
      } catch {
        return;
      }
      if (url.origin !== window.location.origin) {
        return;
      }
      navigate(`${url.pathname}${url.search}${url.hash}`, { replace: true });
    });
  }, [navigate]);
}
